import { Col, Collapse, Divider, Modal, Row, Spin, message } from "antd";
import { AxiosResponse } from "axios";
import React, { Fragment, RefObject } from "react";
import AssociatedEntitiesSection from "../../components/AssociatedEntitiesSection";
import BaseForm from "../../components/Form/BaseForm";
import {
  APIError,
  AddressInterface,
  AssociatedAddress,
  AssociatedEmail,
  AssociatedPhone,
  BranchMap,
  Company,
  Country,
  Customer,
  CustomerBackgroundInformation,
  CustomerCompanyAssociation,
  EmailInterface,
  FinancialInstitution,
  FormFieldConfig,
  Id,
  MaritalStatus,
  Opportunity,
  PhoneInterface,
  SelectOptionsInterface,
  Subscription,
  UpdateBeneficiaryInformationFnProps,
  UpdateCustomerAddressFnProps,
  UpdateCustomerCompanyAssociationFnProps,
  UpdateCustomerEmailFnProps,
  UpdateCustomerPhoneFnProps,
  UpdatePaymentInformationFnProps,
} from "../../helpers/shared-interfaces";

import * as Sentry from "@sentry/browser";
import { FieldData } from "rc-field-form/lib/interface";
import {
  capitalizeFirstLetter,
  dateFormat,
  disabledDate,
  generateDisplayErrorMessage,
  handleValidationErrors,
  request,
  responseHandler,
  subscriptionInitialTags,
  isAutomaticTag,
} from "../../helpers/utilities";

import { FormInstance } from "antd/lib/form";
import moment, { Moment } from "moment";
import CustomerOnboardingDetails from "../../components/CustomerOnboardingDetails";
import StepContainer from "../../components/StepContainer";
import {
  GET_FINANCIAL_INSTITUTIONS,
  GET_ID_TYPES,
  GET_MARITAL_STATUSES,
  GET_TAGS,
  GET_TITLES,
  MARK_ADDRESS_AS_PRIMARY,
  MARK_EMAIL_AS_PRIMARY,
  MARK_PHONE_AS_PRIMARY,
} from "../../gql/general";
import {
  ASSIGN_ADDRESS_TO_CUSTOMER,
  ASSIGN_CUSTOMER_TO_OPPORTUNITY,
  ASSIGN_EMAIL_TO_CUSTOMER,
  ASSIGN_PHONE_TO_CUSTOMER,
  ASSIGN_TAGS_TO_CUSTOMER,
  ASSIGN_TAGS_TO_CUSTOMER_SUBSCRIPTION,
  UNASSIGN_ADDRESS_FROM_CUSTOMER,
  UNASSIGN_EMAIL_FROM_CUSTOMER,
  UNASSIGN_PHONE_FROM_CUSTOMER,
  UPDATE_CUSTOMER_ADDRESS,
  UPDATE_CUSTOMER_EMAIL,
  UPDATE_CUSTOMER_MUTATION,
  UPDATE_CUSTOMER_PHONE,
} from "../../gql/customers";
import { GET_COMPANIES } from "../../gql/companies";
import { UploadFile } from "antd/lib/upload/interface";
import { ghanaCardRegex } from "../../helpers/constants";

const { Panel } = Collapse;
interface State {
  isLoading: boolean;
  showDialog: boolean;
  idToBeDeleted: string | null;
  modalTarget: string | null;
  addressList: AssociatedAddress[];
  emailList: AssociatedEmail[];
  phoneList: AssociatedPhone[];
  financialInstitutions: FinancialInstitution[];
  branchMap: BranchMap;
  customerAccountTags: SelectOptionsInterface[];
  productTags: SelectOptionsInterface[];
  idTypes: Id[];
  companies: Company[];
  maritalStatuses: MaritalStatus[];
  titles: SelectOptionsInterface[];
  signatureUrl?: string
  photoIdUrl?: string
  fatherDeceased?: boolean;
  motherDeceased?: boolean;
  customerBackgroundInformation?: CustomerBackgroundInformation;
  isDobVerified?: boolean;
  dobVerifiedAt?: string;
  verificationProofUrl?: string;
  isVerified?: boolean;
}

interface Props {
  loadOpportunity: () => void;
  customer: Customer;
  opportunity: Opportunity;
  countries: Country[];
  next: () => void;
  prev: () => void;
  setPercentage: (percentage: number) => void;
  getNextViewName: () => string | null;
  getPreviousViewName: () => string | null;
  save?: () => void;
  editingState?: boolean;
  updateCustomerPaymentInformation: (
    props: UpdatePaymentInformationFnProps,
  ) => void;
  updateBeneficiaryInformation?: (
    beneficiaryInfo: UpdateBeneficiaryInformationFnProps,
  ) => void;
  updateCustomerCompanyAssociationsInformation?: (
    props: UpdateCustomerCompanyAssociationFnProps,
  ) => void;
  customerAssociations?: CustomerCompanyAssociation[];
  showBeneficiarySection?: boolean;
  showOnboardingDetailsSection?: boolean;
  showCustomerAssociationSection?: boolean;
  subscriptions?: Subscription[];
  cancelEdit?: () => void;
}

enum mapServerPropToLocalProp {
  dateOfBirth = "Date of Birth",
}

export default class CustomerDetails extends React.Component<Props, State> {
  _isMounted = false;
  reference: RefObject<FormInstance>;
  tagsReference: RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      showDialog: false,
      idToBeDeleted: null,
      modalTarget: null,
      addressList: [],
      emailList: [],
      phoneList: [],
      financialInstitutions: [],
      isLoading: true,
      branchMap: {},
      customerAccountTags: [],
      productTags: [],
      companies: [],
      idTypes: [],
      maritalStatuses: [],
      titles: [],
      fatherDeceased: props.customer.customerBackgroundInformation?.fatherDeceased || false,
      motherDeceased: props.customer.customerBackgroundInformation?.motherDeceased || false,
      verificationProofUrl: props.customer?.verificationProofUrl || "",
    };
    this.reference = React.createRef<FormInstance>();
    this.tagsReference = React.createRef<FormInstance>();

    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
    this.updateCustomer = this.updateCustomer.bind(this);
  }

  setRef = (
    name: "reference" | "tagsReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  phoneAlreadyExists = (id: string): boolean => {
    return this.state.phoneList.some((item: AssociatedPhone) => item.id === id);
  };

  emailAlreadyExists = (id: string): boolean => {
    return this.state.emailList.some((item: AssociatedEmail) => item.id === id);
  };

  addressAlreadyExists = (id: string): boolean => {
    return this.state.addressList.some((item: AssociatedAddress) => item.id === id);
  };

  updateCustomerAddress = async ({
    customerAddress,
    newlyCreatedAddressId,
  }: UpdateCustomerAddressFnProps): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        updateCustomerAddress: {
          customerAddress: {
            customer: Customer;
            address: AddressInterface;
          };
        };
      };
    }

    request({
      query: UPDATE_CUSTOMER_ADDRESS,
      variables: {
        customerAddressId: customerAddress.id,
        addressId: newlyCreatedAddressId,
        customerId: this.props.customer.id,
      },
    }).then(
      async ({ data: { data } }: AxiosResponse<Response>): Promise<void> => {
        if (data.updateCustomerAddress.customerAddress.customer) {
          this.setState({
            isLoading: false,
            addressList:
              data.updateCustomerAddress.customerAddress.customer
                .customerAddresses,
          });
        } else {
          message.error("Request Failed");
          this.setState({ isLoading: false });
        }
      },
    );
  };

  updateCustomerEmail = async ({
    customerEmail,
    newlyCreatedEmailId,
  }: UpdateCustomerEmailFnProps): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        updateCustomerEmail: {
          customerEmail: {
            customer: Customer;
            email: EmailInterface;
          };
        };
      };
    }

    request({
      query: UPDATE_CUSTOMER_EMAIL,
      variables: {
        customerEmailId: customerEmail.id,
        emailId: newlyCreatedEmailId,
        customerId: this.props.customer.id,
      },
    }).then(
      async ({ data: { data } }: AxiosResponse<Response>): Promise<void> => {
        if (data.updateCustomerEmail.customerEmail.customer) {
          this.setState({
            isLoading: false,
            emailList:
              data.updateCustomerEmail.customerEmail.customer.customerEmails,
          });
        } else {
          message.error("Request Failed");
          this.setState({ isLoading: false });
        }
      },
    );
  };

  updateCustomerPhone = async ({
    customerPhone,
    newlyCreatedPhoneId,
  }: UpdateCustomerPhoneFnProps): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        updateCustomerPhone: {
          customerPhone: {
            customer: Customer;
            phone: PhoneInterface;
          };
        };
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: UPDATE_CUSTOMER_PHONE,
        variables: {
          customerPhoneId: customerPhone.id,
          phoneId: newlyCreatedPhoneId,
          customerId: this.props.customer.id,
        },
      });
      if (!data.updateCustomerPhone.customerPhone.customer) {
        throw new Error("Request Failed");
      }
      this.setState({
        isLoading: false,
        phoneList:
          data.updateCustomerPhone.customerPhone.customer.activeCustomerPhones,
      });
    } catch (error) {
      message.error(error.message);
      this.setState({ isLoading: false });
    }
  };

  markEmailAsPrimary = (emailId: string): void => {
    this.setState({ isLoading: true });
    request({
      query: MARK_EMAIL_AS_PRIMARY,
      variables: { emailId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          markCustomerEmailAsPrimary: {
            customerEmails: AssociatedEmail[];
          };
        };
      }>): void => {
        const emailList: AssociatedEmail[] =
          data.data.markCustomerEmailAsPrimary.customerEmails;
        this.setState({ emailList, isLoading: false });
      },
    );
  };

  markAddressAsPrimary = (addressId: string): void => {
    this.setState({ isLoading: true });

    request({
      query: MARK_ADDRESS_AS_PRIMARY,
      variables: { addressId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          markCustomerAddressAsPrimary: {
            customerAddresses: AssociatedAddress[];
          };
        };
      }>): void => {
        const addressList: AssociatedAddress[] =
          data.data.markCustomerAddressAsPrimary.customerAddresses;
        this.setState({ addressList, isLoading: false });
      },
    );
  };

  markPhoneAsPrimary = (phoneId: string): void => {
    this.setState({ isLoading: true });

    request({
      query: MARK_PHONE_AS_PRIMARY,
      variables: { phoneId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          markCustomerPhoneAsPrimary: {
            activeCustomerPhones: AssociatedPhone[];
          };
        };
      }>): void => {
        const phoneList: AssociatedPhone[] =
          data.data.markCustomerPhoneAsPrimary.activeCustomerPhones;
        this.setState({ phoneList, isLoading: false });
      },
    );
  };

  assignAddressToCustomer = (addressId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      customerAddress: AssociatedAddress;
    }

    return request({
      query: ASSIGN_ADDRESS_TO_CUSTOMER,
      variables: { addressId, customerId: this.props.customer.id },
    }).then((response: AxiosResponse): void => {
      const { mutation, err } = responseHandler<Response>(
        response,
        "assignAddressToCustomer",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.setState({ isLoading: false });
        return;
      }

      const customerAddress = mutation.customerAddress;

      if (customerAddress) {
        const addressExists = this.addressAlreadyExists(customerAddress.id);

        if (addressExists) {
          const existingAddress = this.state.addressList.find(
            (address: AssociatedAddress) => address.id === customerAddress.id
          );

          if (existingAddress && !existingAddress.isActive) {
            const updatedAddressList = this.state.addressList.map((address) =>
              address.id === customerAddress.id ? { ...address, isActive: true } : address,
            );
            this.setState({ addressList: updatedAddressList });
            message.success("Address reactivated successfully");
          } else {
            message.error("Address has already been added");
          }
        } else {
          this.setState({
            addressList: [...this.state.addressList, customerAddress],
          });
          message.success("Address assigned successfully");
        }
      } else {
        message.error("Request Failed");
      }

      this.setState({ isLoading: false });
    });
  };

  assignEmailToCustomer = (emailId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      customerEmail: AssociatedEmail;
    }

    return request({
      query: ASSIGN_EMAIL_TO_CUSTOMER,
      variables: { emailId, customerId: this.props.customer.id },
    }).then((response: AxiosResponse): void => {
      const { mutation, err } = responseHandler<Response>(
        response,
        "assignEmailToCustomer",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.setState({ isLoading: false });
        return;
      }

      const customerEmail = mutation.customerEmail;

      if (customerEmail) {
        const emailExists = this.emailAlreadyExists(customerEmail.id);

        if (emailExists) {
          const existingEmail = this.state.emailList.find(
            (email: AssociatedEmail) => email.id === customerEmail.id
          );
          if (existingEmail && !existingEmail.isActive) {
            const updatedEmailList = this.state.emailList.map((email) =>
              email.id === customerEmail.id ? { ...email, isActive: true } : email,
            );
            this.setState({ emailList: updatedEmailList });
            message.success("Email reactivated successfully");
          } else {
            message.error("Email has already been added");
          }
        } else {
          this.setState({
            emailList: [...this.state.emailList, customerEmail],
          });
          message.success("Email assigned successfully");
        }
      } else {
        message.error("Request Failed");
      }
      this.setState({ isLoading: false });
    });
  };

  assignPhoneToCustomer = async (phoneId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      customerPhone: AssociatedPhone;
    }

    const response: AxiosResponse = await request({
      query: ASSIGN_PHONE_TO_CUSTOMER,
      variables: { phoneId, customerId: this.props.customer.id },
    });

    const { mutation, err } = responseHandler<Response>(response, "assignPhoneToCustomer");

    if (err) {
      message.error(generateDisplayErrorMessage(err));
      this.setState({ isLoading: false });
      return;
    }

    if (mutation) {
      const customerPhone = mutation.customerPhone;

      if (customerPhone) {
        const phoneExists = this.phoneAlreadyExists(customerPhone.id);

        if (phoneExists) {
          const existingPhone = this.state.phoneList.find(
            (phone: AssociatedPhone) => phone.id === customerPhone.id
          );
          if (existingPhone && !existingPhone.isActive) {
            const updatedPhoneList = this.state.phoneList.map((phone) =>
              phone.id === customerPhone.id ? { ...phone, isActive: true } : phone,
            );
            this.setState({ phoneList: updatedPhoneList });
            message.success("Phone number reactivated successfully");
          } else {
            message.error("Phone number has already been added");
          }
        } else {
          this.setState({
            phoneList: [...this.state.phoneList, customerPhone],
          });
          message.success("Phone number assigned successfully");
        }
      } else {
        message.error("Request Failed");
      }
    }

    this.setState({ isLoading: false });
  };

  displayDefaultPickerValue(): Moment {
    const yearsBefore = moment().subtract(18, "year");
    return moment(yearsBefore, dateFormat);
  }

  unAssignAddressFromCustomer = async (addressId: string) => {
    const addressList: AssociatedAddress[] = this.state.addressList;

    const customerAddress: AssociatedAddress | undefined = addressList.find(
      (customerAddress: AssociatedAddress): boolean => {
        return customerAddress.address.id === addressId;
      },
    );

    if (customerAddress) {
      interface Response {
        customerAddresses: AssociatedAddress[];
      }
      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query: UNASSIGN_ADDRESS_FROM_CUSTOMER,
        variables: { customerAddressId: customerAddress.id },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "unassignAddressFromCustomer",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.setState({ isLoading: false });
        return;
      }

      if (mutation) {
        this.setState({
          addressList: mutation.customerAddresses,
        });
        message.success("Address removed successfully");
      } else message.error("Failed to deactivate address");
    }
    this.setState({ isLoading: false });
  };

  unAssignEmailFromCustomer = async (emailId: string) => {
    const emailList: AssociatedEmail[] = this.state.emailList;

    const customerEmail: AssociatedEmail | undefined = emailList.find(
      (customerEmail: AssociatedEmail): boolean => {
        return customerEmail.email.id === emailId;
      },
    );

    if (customerEmail) {
      interface Response {
        customerEmails: AssociatedEmail[];
      }
      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query: UNASSIGN_EMAIL_FROM_CUSTOMER,
        variables: { customerEmailId: customerEmail.id },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "unassignEmailFromCustomer",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.setState({ isLoading: false });
        return;
      }

      if (mutation) {
        this.setState({
          emailList: mutation.customerEmails,
        });
        message.success("Email removed successfully");
      } else message.error("Failed to deactivate email");
    }
    this.setState({ isLoading: false });
  };

  unAssignPhoneFromCustomer = async (phoneId: string) => {
    const phoneList: AssociatedPhone[] = this.state.phoneList;

    const customerPhone: AssociatedPhone | undefined = phoneList.find(
      (customerPhone: AssociatedPhone): boolean => {
        return customerPhone.phone.id === phoneId;
      },
    );

    if (customerPhone) {
      interface Response {
        activeCustomerPhones: AssociatedPhone[];
      }

      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query: UNASSIGN_PHONE_FROM_CUSTOMER,
        variables: { customerPhoneId: customerPhone.id },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "unassignPhoneFromCustomer",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.setState({ isLoading: false });
        return;
      }

      if (mutation) {
        this.setState({
          phoneList: mutation.activeCustomerPhones,
        });
        message.success("Phone number removed successfully");
      } else message.error("Failed to deactivate phone number");
    }
    this.setState({ isLoading: false });
  };

  updateCustomer<T>(data: Record<string, unknown>): Promise<AxiosResponse<T>> {
    const {
      signatureUrl,
      photoIdUrl,
      verificationProofUrl,
    } = this.state;

    let variables = {
      ...data,
      signatureUrl,
      photoIdUrl,
      verificationProofUrl,
    };

    return request({
      query: UPDATE_CUSTOMER_MUTATION,
      variables,
    });
  }

  assignCustomerToOpportunity = (
    customerId: string,
  ): Promise<AxiosResponse<void>> => {
    const { opportunity } = this.props;
    return request({
      query: ASSIGN_CUSTOMER_TO_OPPORTUNITY,
      variables: {
        opportunityId: opportunity.id,
        customerId,
      },
    });
  };

  generateBranchMap = async (): Promise<void> => {
    const branchMap: BranchMap = {};

    this.state.financialInstitutions.forEach(
      (institution: FinancialInstitution): void => {
        branchMap[institution.id] = institution.bankBranches;
      },
    );

    this.setState({ branchMap });
  };

  async getFinancialInstitutionals(): Promise<void> {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      this.setState({
        financialInstitutions: data.financialInstitutions,
        isLoading: false,
      });
      this.generateBranchMap();
    } catch (error) { }
     finally {
      this.setState({
        isLoading: false,
      });
    }
  }

  async getAllCompanies(): Promise<void> {
    request({
      query: GET_COMPANIES,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        companies: data.companies,
        isLoading: false,
      });
    });
  }

  async getTitles(): Promise<void> {
    interface Response {
      data: {
        titles: SelectOptionsInterface[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TITLES,
      });

      this.setState({
        titles: data.titles,
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to get titles  - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  }

  async getMaritalStatuses(): Promise<void> {
    interface Response {
      data: {
        maritalStatuses: MaritalStatus[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_MARITAL_STATUSES,
      });

      this.setState({
        maritalStatuses: data.maritalStatuses,
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to get marital statuses  - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  }

  async getCustomerAccountTags(): Promise<void> {
    interface Response {
      data: {
        tags: SelectOptionsInterface[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TAGS,
        variables: {
          forCustomer: true,
          forAccount: true,
        },
      });

      this.setState({
        customerAccountTags: data.tags,
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to get customer account tags - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  }

  async getIdTypes(): Promise<void> {
    interface Response {
      data: {
        idTypes: Id[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_ID_TYPES,
      });

      this.setState({
        idTypes: data.idTypes,
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to get ids - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  }

  async getProductTags(): Promise<void> {
    interface Response {
      data: {
        tags: SelectOptionsInterface[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TAGS,
        variables: {
          forProduct: true,
        },
      });

      this.setState({
        productTags: data.tags.filter((tag) => !isAutomaticTag(tag.name)),
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to get product tags  - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  }

  handleCustomerTagChange = async (value: string): Promise<void> => {
    const values = value as unknown as string[];

    interface Response {
      customer: Customer;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: ASSIGN_TAGS_TO_CUSTOMER,
        variables: {
          customerId: this.props.customer.id,
          tagIds: values,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignTagsToCustomer",
      );

      if (err) {
        message.error(`Failed to assign tag - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      message.error(`Failed to assign tag - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  assignTagToCustomerSubscription = async (
    tagIds: string[],
    subscriptionId: string,
  ): Promise<void> => {
    interface Response {
      customerSubscription: Subscription;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: ASSIGN_TAGS_TO_CUSTOMER_SUBSCRIPTION,
        variables: {
          customerSubscriptionId: subscriptionId,
          tagIds,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignTagsToCustomerSubscription",
      );

      if (err) {
        message.error(`Failed to assign tag - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  handleSubscriptionTagChange = (subscriptionId: string) => {
    return (tagIds: string): Promise<void> => {
      const ids = tagIds as unknown as string[];
      return this.assignTagToCustomerSubscription(ids, subscriptionId);
    };
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handleDelete = (file: UploadFile): void => {
    let newState = {};

    switch (file.name) {
      case "Customer Signature":
        newState = { signatureUrl: "" };
        break;
      case "Verification Proof":
        newState = { verificationProofUrl: "" };
        break;
      default:
        newState = { photoIdUrl: "" };
    }

    this.setState(newState);
  };

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    await Promise.all([
      this.getFinancialInstitutionals(),
      this.getCustomerAccountTags(),
      this.getProductTags(),
      this.getAllCompanies(),
      this.getIdTypes(),
      this.getMaritalStatuses(),
      this.getTitles(),
    ]);

    const { customer, setPercentage } = this.props;
    setPercentage(60);

    this._isMounted &&
      this.setState({
        addressList: customer.customerAddresses,
        phoneList: customer.activeCustomerPhones,
        emailList: customer.customerEmails,
        customerBackgroundInformation: customer.customerBackgroundInformation,
        isLoading: false,
      });
  }

  setBackgroundInfo = (backgroundInfo: CustomerBackgroundInformation) => {
    this.setState({
      customerBackgroundInformation: backgroundInfo,
    });
  };

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  async next(): Promise<void> {
    const { customer } = this.props;

    interface UpdateCustomerResponse {
      customer: Customer;
    }

    const fieldsToBeValidated: string[] = [
      "firstName",
      "lastName",
      "ssnitNumber",
      "dateOfBirth",
      "countryId",
      "gender",
      "middleName",
      "maritalStatusId",
      "idTypeId",
      "photoIdNumber",
      "photoIdUrl",
      "signatureUrl",
      "titleId",
      "ghanaCardNo",
      "idIssueDate",
      "idExpiryDate",
      "fatherName",
      "fatherDeceased",
      "fatherPhoneNumber",
      "motherName",
      "motherDeceased",
      "motherPhoneNumber",
      "previousSsnitNumber",
      "hometown",
      "nextOfKinName",
      "nextOfKinPhoneNumber",
      "previousEmployer",
      "isVerified",
      "verificationProofUrl",
    ];

    if (!this.reference.current) return;

    let data;

    const form = this.reference.current;
    if (form) {
      const isVerified = form.getFieldValue("isVerified");
      const proofUrl = form.getFieldValue("verificationProofUrl");

      if (isVerified && !proofUrl && !this.state.verificationProofUrl) {
        message.error(
          "Proof of Verification is required when marking as verified",
        );
        form.setFields([
          {
            name: "verificationProofUrl",
            errors: ["Please attach proof of verification"],
          },
        ]);
        return;
      }
    }

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }
    this.setState({ isLoading: true });

    try {
      const payload = {
        ...data,
        dateOfBirth: data.dateOfBirth && data.dateOfBirth.split("T")[0],
        idIssueDate: data.idIssueDate && data.idIssueDate.split("T")[0],
        idExpiryDate: data.idExpiryDate && data.idExpiryDate.split("T")[0],
        maritalStatusId: data.maritalStatusId,
        customerId: customer.id,
        isVerified: this.state.isVerified || data.isVerified || !!customer.verificationProofUrl,
      };

      const response: AxiosResponse<UpdateCustomerResponse> =
        await this.updateCustomer(payload);
      this.setState({ isLoading: false });

      const { err, handled } = responseHandler<UpdateCustomerResponse>(
        response,
        "updateCustomer",
      );

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        const resolvedErrors: FieldData[] = [];
        err.forEach((err: APIError): void => {
          resolvedErrors.push({
            name: err.property,
            errors: [
              `${mapServerPropToLocalProp[
                  err.property as keyof typeof mapServerPropToLocalProp
                ]
              } ${err.description}`,
            ],
          });
        });
        this.reference.current.setFields(resolvedErrors);
      } else {
        if (this.props.editingState && this.props.save) {
          this.props.save();
        } else {
          this.props.next();
        }
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to create user - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  toggleModal = (): void => {
    this.setState({ showDialog: !this.state.showDialog });
  };

  prev(): void {
    this.toggleModal();
  }

  handleFatherDeceasedChange = (checkedValue: boolean): void => {
    this.setState({ fatherDeceased: checkedValue });
  };

  handleMotherDeceasedChange = (checkedValue: boolean): void => {
    this.setState({ motherDeceased: checkedValue });
  };

  changeIsVerified = async (checkedValue: boolean) => {
    this.setState({
      isVerified: checkedValue,
    });
  };

  render(): JSX.Element {
    const {
      addressList,
      emailList,
      phoneList,
      isLoading,
      showDialog,
      financialInstitutions,
      customerBackgroundInformation,
      isVerified,
    } = this.state;
    const { countries, customer, editingState } = this.props;

    const fileList =
      customer.photoIdUrl && customer.idType
        ? [
            {
              size: 1000,
              type: customer.idType.name,
              uid: "1",
              url: customer.photoIdUrl,
              name: customer.idType.name,
              thumbUrl: customer.photoIdUrl,
            },
          ]
        : [];

    const signatureUpload = customer.signatureUrl
      ? [
          {
            size: 1000,
            type: customer.signatureUrl,
            uid: "1",
            url: customer.signatureUrl,
            name: "Customer Signature",
            thumbUrl: customer.signatureUrl,
          },
        ]
      : [];

    const customerBioFields: FormFieldConfig[] = [
      {
        id: "titleId",
        name: "titleId",
        inputType: "select",
        rule: "string",
        required: false,
        placeholder: "Title",
        label: "Title",
        selectOptions: this.state.titles,
        initialValue: customer.title
          ? capitalizeFirstLetter(customer.title.id)
          : undefined,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "firstName",
        name: "firstName",
        inputType: "string",
        rule: "string",
        placeholder: "First Name",
        label: "First Name",
        initialValue: customer.firstName,

        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "middleName",
        name: "middleName",
        inputType: "string",
        rule: "string",
        placeholder: "Middle Name",
        initialValue: customer.middleName,

        label: "Middle Name",
        required: false,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "lastName",
        name: "lastName",
        inputType: "string",
        rule: "string",
        placeholder: "Last Name",
        initialValue: customer.lastName,

        label: "Last Name",
        required: true,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "ssnitNumber",
        name: "ssnitNumber",
        inputType: "string",
        rule: "string",
        initialValue: customer.ssnitNumber,
        required: false,
        disabled: false,
        placeholder: "SSNIT Number",
        label: "SSNIT Number",
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "dateOfBirth",
        name: "dateOfBirth",
        inputType: "date",
        rule: "string",
        placeholder: "Date of birth",
        label: "Date of birth",
        initialValue: customer.dateOfBirth || undefined,
        required: false,
        disabledDate: disabledDate,
        dateOptions: {
          limitTo18: true,
        },
        defaultPickerValue: this.displayDefaultPickerValue(),
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "maritalStatusId",
        name: "maritalStatusId",
        inputType: "select",
        rule: "string",
        required: false,
        placeholder: "Marital Status",
        label: "Marital Status",
        selectOptions: this.state.maritalStatuses,
        initialValue: customer.maritalStatus
          ? customer.maritalStatus.id
          : undefined,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "countryId",
        name: "countryId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Nationality",
        label: "Nationality",
        selectOptions: countries,
        initialValue: customer.country ? customer.country.id : undefined,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "gender",
        name: "gender",
        inputType: "select",
        rule: "string",
        placeholder: "Gender",
        label: "Gender",
        initialValue: customer.gender ? customer.gender : undefined,
        selectOptions: [
          { id: "0", name: "Male", value: "male" },
          { id: "1", name: "Female", value: "female" },
        ],
        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "photoIdNumber",
        name: "photoIdNumber",
        inputType: "string",
        rule: "string",
        initialValue: customer.photoIdNumber,
        required: false,
        disabled: false,
        placeholder: "Enter ID Number",
        label: "ID Number",
        colConfig: {
          span: 7,
        },
      },
      {
        id: "photoIdUrl",
        name: "photoIdUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: customer.photoIdUrl,
        required: false,
        disabled: false,
        label: "ID Upload",
        changeHandler: (combinedInfo: string) => this.handleFileUploadChange("photoIdUrl", combinedInfo),
        defaultFileList: fileList,
        onFileDelete: this.handleDelete,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "ghanaCardNo",
        name: "ghanaCardNo",
        inputType: "string",
        rule: "string",
        initialValue: customer.ghanaCardNo,
        required: false,
        disabled: false,
        placeholder: "Enter Ghana Card ID Number",
        label: "Ghana Card ID Number",
        pattern: ghanaCardRegex,
        patternErrorMessage: "Invalid Ghana Card Number format. Expected format: ABC-123456789-1",
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "idIssueDate",
        name: "idIssueDate",
        inputType: "date",
        rule: "string",
        placeholder: "Ghana Card Issue Date",
        label: "Ghana Card Issue Date",
        initialValue: customer?.idIssueDate,
        required: false,
        elementStyle: {
          width: "100%",
        },
        dateOptions: {
          limitTo18: false,
          startYear: 2017,
        },
        colConfig: {
          span: 7,
        },
      },
      {
        id: "idExpiryDate",
        name: "idExpiryDate",
        inputType: "date",
        rule: "string",
        placeholder: "Ghana Card Expiry Date",
        label: "Ghana Card Expiry Date",
        initialValue: customer?.idExpiryDate,
        required: false,
        elementStyle: {
          width: "100%",
        },
        dateOptions: {
          limitTo18: false,
          startYear: 2017,
        },
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "signatureUrl",
        name: "signatureUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: customer.signatureUrl,
        required: false,
        disabled: false,
        label: "Signature Upload",
        defaultFileList: signatureUpload,
        changeHandler: (combinedInfo: string) => this.handleFileUploadChange("signatureUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "isVerified",
        name: "isVerified",
        inputType: "checkbox",
        rule: "boolean",
        label: "Ghana Card & Date of Birth Verified?",
        checkboxCheckedValue: isVerified || !!customer.verificationProofUrl,
        required: false,
        checkboxChangeHandler: (e) => this.changeIsVerified(e.target.checked),
        colConfig: {
          span: 7,
        },
      },
      {
        id: "verificationProofUrl",
        name: "verificationProofUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: customer.verificationProofUrl,
        required: isVerified || false,
        disabled: false,
        label: "Proof of Verification",
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("verificationProofUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        defaultFileList: customer.verificationProofUrl
          ? [
              {
                size: 1000,
                type: "Verification Proof",
                uid: "1",
                url: customer.verificationProofUrl,
                name: "Verification Proof",
                thumbUrl: customer.verificationProofUrl,
              },
            ]
          : [],
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
    ];

    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.props.getNextViewName}
          getPreviousViewName={this.props.getPreviousViewName}
          cancelEdit={this.props.cancelEdit}
          config={{
            prev: this.prev,
            next: this.next,
            save: this.next,
            showNext: !editingState,
            showPrev: !editingState,
            showSave: editingState,
            showCancelEdit: true,
          }}
        >
          <Fragment>
            <Modal
              title="Confirm Action"
              visible={showDialog}
              {...{
                okText: "Yes",
                okType: "danger",
                cancelButtonProps: {
                  className: "modal-cancel-btn",
                },
                okButtonProps: {
                  className: "modal-confirm-btn",
                },
              }}
              onOk={this.props.prev}
              onCancel={this.toggleModal}
            >
              <div>
                You may lose some of the information you’ve entered on this
                page. Do you still want to proceed?
              </div>
            </Modal>

            <Row>
              <Col>
                <div className="sub-form" style={{ marginBottom: "35px" }}>
                  <BaseForm
                    fields={customerBioFields}
                    reference={this.reference}
                    fieldsToHide={[]}
                    layout="vertical"
                  ></BaseForm>
                  <Divider />
                  <Collapse
                    style={{
                      marginBottom: "12px",
                    }}
                  >
                    {this.props.editingState &&
                      this.props.subscriptions &&
                      this.props.subscriptions.map(
                        (subscription: Subscription) => (
                          <Panel
                            header={<span>{subscription.product.name}</span>}
                            key={subscription.id}
                          >
                            <BaseForm
                              layout="horizontal"
                              reference={this.tagsReference}
                              fields={[
                                {
                                  isArrayField: true,
                                  id: "tags",
                                  name: "tags",
                                  inputType: "multipleSelect",
                                  label: "Tags",
                                  rule: "string",
                                  placeholder: "Select Tags",
                                  required: false,
                                  initialValue: subscriptionInitialTags(
                                    subscription.customerSubscriptionTags,
                                  ),
                                  changeHandler:
                                    this.handleSubscriptionTagChange(
                                      subscription.id,
                                    ),
                                  colConfig: {
                                    span: 24,
                                  },
                                  selectOptions: this.state.productTags,
                                  elementStyle: {
                                    marginBottom: "20px",
                                  },
                                },
                              ]}
                              fieldsToHide={[]}
                            />
                          </Panel>
                        ),
                      )}
                  </Collapse>

                  <AssociatedEntitiesSection
                    editingState={editingState}
                    emailList={emailList}
                    addressList={addressList}
                    phoneList={phoneList}
                    countries={countries}
                    customerBackgroundInformation={customerBackgroundInformation}
                    setBackgroundInfo={this.setBackgroundInfo}
                    customer={{
                      customer,
                      updateCustomerPhone: this.updateCustomerPhone,
                      updateCustomerEmail: this.updateCustomerEmail,
                      updateCustomerAddress: this.updateCustomerAddress,
                      assignEmailToCustomer: this.assignEmailToCustomer,
                      assignAddressToCustomer: this.assignAddressToCustomer,
                      assignPhoneToCustomer: this.assignPhoneToCustomer,
                      unAssignAddressToCustomer:
                        this.unAssignAddressFromCustomer,
                      unAssignEmailToCustomer: this.unAssignEmailFromCustomer,
                      unAssignPhoneToCustomer: this.unAssignPhoneFromCustomer,
                      markPhoneAsPrimary: this.markPhoneAsPrimary,
                      markAddressAsPrimary: this.markAddressAsPrimary,
                      markEmailAsPrimary: this.markEmailAsPrimary,
                      reactivatePhone: this.assignPhoneToCustomer,
                      reactivateEmail: this.assignEmailToCustomer,
                      reactivateAddress: this.assignAddressToCustomer,
                    }}
                  />
                </div>
                <Divider />
                <CustomerOnboardingDetails
                  loadOpportunity={this.props.loadOpportunity}
                  branchMap={this.state.branchMap}
                  financialInstitutions={financialInstitutions}
                  updateCustomerPaymentInformation={
                    this.props.updateCustomerPaymentInformation
                  }
                  updateCustomerCompanyAssociationsInformation={
                    this.props.updateCustomerCompanyAssociationsInformation
                  }
                  opportunity={this.props.opportunity}
                  isEditing={this.props.editingState || false}
                  showBeneficiarySection={this.props.showBeneficiarySection}
                  showCustomerAssociationSection={
                    this.props.showCustomerAssociationSection
                  }
                  showOnboardingDetailsSection={
                    this.props.showOnboardingDetailsSection
                  }
                  subscriptions={this.props.subscriptions || []}
                  updateBeneficiaryInformation={
                    this.props.updateBeneficiaryInformation
                  }
                  customerAssociations={this.props.customerAssociations || []}
                  companies={this.state.companies}
                  customer={this.props.customer}
                />
              </Col>
            </Row>
          </Fragment>
        </StepContainer>
      </Spin>
    );
  }
}
